import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { Favourite } from "../../services/favouritesService";

export interface FavouritesState {
    items: Favourite[];
    isLoading: boolean;
    isLoadingMore: boolean;
    error: string | null;
    lastLoadedUserId: string | null;
    page: number;
    hasMore: boolean;
}

const initialState: FavouritesState = {
    items: [],
    isLoading: false,
    isLoadingMore: false,
    error: null,
    lastLoadedUserId: null,
    page: 1,
    hasMore: false,
};

const favouritesSlice = createSlice({
    name: "favourites",
    initialState,
    reducers: {
        startLoadingFavourites(state, action: PayloadAction<{ userId: string }>) {
            state.isLoading = true;
            state.isLoadingMore = false;
            state.error = null;
            state.lastLoadedUserId = action.payload.userId;
            state.page = 1;
            state.hasMore = false;
        },
        startLoadingMoreFavourites(state) {
            state.isLoadingMore = true;
            state.error = null;
        },
        setFavourites(state, action: PayloadAction<Favourite[]>) {
            state.items = action.payload;
            state.isLoading = false;
            state.isLoadingMore = false;
            state.error = null;
        },
        setFavouritesPage(
            state,
            action: PayloadAction<{ items: Favourite[]; page: number; hasMore: boolean }>
        ) {
            state.items = action.payload.items;
            state.page = action.payload.page;
            state.hasMore = action.payload.hasMore;
            state.isLoading = false;
            state.isLoadingMore = false;
            state.error = null;
        },
        appendFavourites(
            state,
            action: PayloadAction<{ items: Favourite[]; page: number; hasMore: boolean }>
        ) {
            const seen = new Set(state.items.map((item) => String(item.id || item.imageUrl)));
            action.payload.items.forEach((item) => {
                const key = String(item.id || item.imageUrl);
                if (seen.has(key)) return;
                seen.add(key);
                state.items.push(item);
            });
            state.page = action.payload.page;
            state.hasMore = action.payload.hasMore;
            state.isLoadingMore = false;
            state.error = null;
        },
        setFavouritesError(state, action: PayloadAction<string | null>) {
            state.error = action.payload;
            state.isLoading = false;
            state.isLoadingMore = false;
        },
        clearFavourites(state) {
            state.items = [];
            state.error = null;
            state.isLoading = false;
            state.isLoadingMore = false;
            state.lastLoadedUserId = null;
            state.page = 1;
            state.hasMore = false;
        },
    },
});

export const {
    startLoadingFavourites,
    startLoadingMoreFavourites,
    setFavourites,
    setFavouritesPage,
    appendFavourites,
    setFavouritesError,
    clearFavourites,
} = favouritesSlice.actions;

export default favouritesSlice.reducer;
